import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getChecklist,
  getReport,
  listCampaignLinks,
  sendCampaign,
  sendTest,
} from '@/lib/campaigns-api';
import { keys } from './use-campaigns';

export const sendKeys = {
  checklist: (campaignId: string) => ['checklist', campaignId] as const,
  report: (campaignId: string) => ['report', campaignId] as const,
  links: (campaignId: string) => ['campaign-links', campaignId] as const,
};

/**
 * Every link in the campaign, document order.
 *
 * Not folded into the report query: the report is the summary and loads on every visit, while the
 * full link table is a detail somebody opens. Keeping them separate means the common case does not
 * pay for the uncommon one.
 */
export function useCampaignLinks(campaignId: string) {
  return useQuery({
    queryKey: sendKeys.links(campaignId),
    queryFn: () => listCampaignLinks(campaignId, { take: 200 }),
  });
}

/**
 * The pre-send checklist.
 *
 * `staleTime: 0` on purpose. Everything it reports can change from under the author — a colleague
 * approves it, a contact unsubscribes, the breaker trips — and a cached checklist beside a send
 * button is the number somebody approves against.
 */
export function useChecklist(campaignId: string) {
  return useQuery({
    queryKey: sendKeys.checklist(campaignId),
    queryFn: () => getChecklist(campaignId),
    staleTime: 0,
  });
}

export function useReport(campaignId: string) {
  return useQuery({
    queryKey: sendKeys.report(campaignId),
    queryFn: () => getReport(campaignId),
    // A live send's numbers move. Thirty seconds is often enough to feel current without hammering
    // an aggregate query on every focus change.
    refetchInterval: 30_000,
  });
}

/**
 * Enqueues the dispatch.
 *
 * Emphatically NOT optimistic. This is the one action in the application that cannot be undone, so
 * the UI must reflect what the server actually accepted rather than what it hoped for.
 */
export function useSendCampaign(campaignId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => sendCampaign(campaignId),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.campaign(campaignId) });
      await client.invalidateQueries({ queryKey: sendKeys.checklist(campaignId) });
    },
  });
}

export function useSendTest(campaignId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { armId: string; toEmail: string }) =>
      sendTest({ campaignId, armId: input.armId, toEmail: input.toEmail }),
    onSuccess: async () => {
      // The checklist's test-send item changes as a result, so it has to be refetched.
      await client.invalidateQueries({ queryKey: sendKeys.checklist(campaignId) });
    },
  });
}
